import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { del, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Badge, Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

export interface PatientContact {
  id: string;
  name: string;
  relation: string;
  phone: string | null;
  cnic: string | null;
  canConsent: boolean;
  isPrimary: boolean;
}

export const RELATIONS = ['mother', 'father', 'guardian', 'spouse', 'son', 'daughter', 'brother', 'sister', 'grandparent', 'attendant', 'emergency contact', 'other'];

export function PatientContactsList({ patientId, contacts, canEdit }: { patientId: string; contacts: PatientContact[]; canEdit: boolean }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
  const [removing, setRemoving] = useState<PatientContact | null>(null);

  const makePrimary = useMutation({
    mutationFn: (id: string) => put(`/patients/${patientId}/contacts/${id}`, { isPrimary: true }),
    onSuccess: () => {
      toast.success(tr('opd', 'primary_guardian_changed'));
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_change_the_primary_guardian'), describeError(error)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del(`/patients/${patientId}/contacts/${id}`),
    onSuccess: () => {
      toast.success(tr('opd', 'contact_removed'));
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_remove_the_contact'), describeError(error)),
  });

  return (
    <div className="space-y-1.5">
      {contacts.length === 0 && <p className="text-muted-foreground">{tr('opd', 'no_guardian_or_contact_on_record')}</p>}
      {contacts.map((c) => (
        <div key={c.id} className="flex items-start justify-between gap-2 rounded-md border px-2 py-1.5">
          <span>
            <span className="font-medium">{c.name}</span>
            <span className="block text-xs text-muted-foreground">
              {c.relation}
              {c.phone ? ` · ${c.phone}` : ''}
              {c.cnic ? ` · ${c.cnic}` : ''}
              {!c.canConsent ? ' · cannot consent' : ''}
            </span>
          </span>
          <span className="flex flex-col items-end gap-1">
            {c.isPrimary ? (
              <Badge variant="success">{tr('opd', 'primary')}</Badge>
            ) : (
              canEdit && (
                <button type="button" className="text-xs underline-offset-4 hover:underline" onClick={() => makePrimary.mutate(c.id)}>
                  {tr('opd', 'make_primary')}
                </button>
              )
            )}
            {canEdit && !c.isPrimary && (
              <button type="button" className="text-xs text-destructive underline-offset-4 hover:underline" onClick={() => setRemoving(c)}>
                {tr('opd', 'remove_2')}
              </button>
            )}
          </span>
        </div>
      ))}
      {removing && (
        <ConfirmDialog
          subject={removing.name}
          destructive
          confirmLabel={tr('opd', 'remove_2')}
          onConfirm={() => remove.mutateAsync(removing.id)}
          onClose={() => setRemoving(null)}
        />
      )}
      {canEdit && (
        <Button variant="outline" size="sm" className="w-full" onClick={() => navigate(`/patients/${patientId}/contacts/new`)}>
          {tr('opd', 'add_guardian_contact')}
        </Button>
      )}
    </div>
  );
}
